import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../apiClient';
import { UserContext } from '../../UserContext';
import { toast } from 'react-toastify';
import './KallelseTemplateForm.css';

function KallelseTemplateForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const isEditing = !!id;

  // Form state
  const [templateName, setTemplateName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [clinicId, setClinicId] = useState('');
  const [clinics, setClinics] = useState([]);
  const [variables, setVariables] = useState({ grouped: {} });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Refs för att spåra vilken input som är aktiv
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const [activeInput, setActiveInput] = useState('body'); // 'subject' eller 'body'

  // Hämta kliniker och variabler
  useEffect(() => {
    apiClient.get('/api/clinics')
      .then(res => setClinics(res.data))
      .catch(err => console.error('Fel vid hämtning av kliniker:', err));

    apiClient.get('/api/kallelsemallar/variables')
      .then(res => setVariables(res.data))
      .catch(err => console.error('Fel vid hämtning av variabler:', err));
  }, []);

  // Förifyll komplett, redigerbar exempeltext vid SKAPA ny (inte vid redigering).
  // Ersätter den gamla placeholder-texten som försvann så fort man skrev.
  useEffect(() => {
    if (id) return; // redigering hämtar befintligt innehåll nedan
    apiClient.get('/api/kallelsemallar/defaults')
      .then(res => {
        const d = res.data?.kallelse || {};
        // prev || … så vi aldrig skriver över det användaren redan hunnit skriva
        setSubject(prev => prev || d.subject || '');
        setBody(prev => prev || d.body || '');
      })
      .catch(err => console.error('Fel vid hämtning av standardinnehåll:', err));
  }, [id]);

  // Hämta befintlig mall vid redigering
  useEffect(() => {
    if (id) {
      apiClient.get(`/api/kallelsemallar/${id}`)
        .then(res => {
          const data = res.data;
          setTemplateName(data.template_name || '');
          setSubject(data.subject || '');
          setBody(data.body || '');
          setClinicId(data.clinic_id || '');
        })
        .catch(err => {
          console.error('Fel vid hämtning av mall:', err);
          setError('Kunde inte hämta mallen');
        });
    }
  }, [id]);

  // Infoga variabel vid markörposition
  const insertVariable = (variable) => {
    if (activeInput === 'subject' && subjectRef.current) {
      const input = subjectRef.current;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const newValue = subject.slice(0, start) + variable + subject.slice(end);
      setSubject(newValue);
      // Sätt markör efter infogad variabel
      setTimeout(() => {
        input.selectionStart = input.selectionEnd = start + variable.length;
        input.focus();
      }, 0);
    } else if (activeInput === 'body' && bodyRef.current) {
      const textarea = bodyRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = body.slice(0, start) + variable + body.slice(end);
      setBody(newValue);
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + variable.length;
        textarea.focus();
      }, 0);
    }
  };

  // Spara mall
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!templateName.trim()) {
      toast.error('Mallnamn krävs');
      return;
    }
    if (!subject.trim()) {
      toast.error('Ämnesrad krävs');
      return;
    }
    if (!body.trim()) {
      toast.error('Mallinnehåll krävs');
      return;
    }

    setSaving(true);
    setError('');

    const payload = {
      template_name: templateName,
      subject: subject,
      body: body,
      clinic_id: clinicId || null
    };

    try {
      if (isEditing) {
        await apiClient.put(`/api/kallelsemallar/${id}`, payload);
        toast.success('Kallelsemall uppdaterad');
      } else {
        await apiClient.post('/api/kallelsemallar', payload);
        toast.success('Kallelsemall skapad');
      }
      navigate('/kallelsemallar');
    } catch (err) {
      console.error('Fel vid sparande:', err);
      toast.error(err.response?.data?.msg || 'Fel vid sparande');
      setError(err.response?.data?.msg || 'Fel vid sparande');
    } finally {
      setSaving(false);
    }
  };

  const groupedVariables = variables.grouped || {};

  return (
    <div className="kallelse-form-container">
      <div className="kallelse-form-main">
        <h2>{isEditing ? 'Redigera kallelsemall' : 'Skapa kallelsemall'}</h2>

        {error && <p className="error-message">{error}</p>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="templateName">Mallnamn *</label>
            <input
              type="text"
              id="templateName"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="T.ex. Bokningsbekräftelse"
              required
            />
          </div>

          {user?.is_admin && (
            <div className="form-group">
              <label htmlFor="clinicId">Klinik</label>
              <select
                id="clinicId"
                value={clinicId}
                onChange={(e) => setClinicId(e.target.value)}
              >
                <option value="">Global mall (alla kliniker)</option>
                {clinics.map(c => (
                  <option key={c.clinic_id} value={c.clinic_id}>
                    {c.clinic_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="subject">Ämnesrad *</label>
            <input
              ref={subjectRef}
              type="text"
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onFocus={() => setActiveInput('subject')}
              placeholder="T.ex. Bokningsbekräftelse - {besökstyp} den {datum}"
              required
            />
            <small className="hint">Använd variabler som {'{patientnamn}'} och {'{datum}'}</small>
          </div>

          <div className="form-group">
            <label htmlFor="body">Mallinnehåll *</label>
            <textarea
              ref={bodyRef}
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onFocus={() => setActiveInput('body')}
              placeholder={`Hej {patientnamn}!

Du är bokad för {besökstyp} den {datum} kl {tid}.

Plats: {kliniknamn}
Behandlare: {behandlare}

Välkommen!`}
              rows={12}
              required
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/kallelsemallar')}
              className="cancel-button"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={saving}
              className="save-button"
            >
              {saving ? 'Sparar...' : (isEditing ? 'Uppdatera' : 'Skapa mall')}
            </button>
          </div>
        </form>
      </div>

      <div className="variable-sidebar">
        <h3>Tillgängliga variabler</h3>
        <p className="variable-help">Klicka för att infoga i {activeInput === 'subject' ? 'ämnesrad' : 'mallinnehåll'}</p>

        <div className="variable-instruktion">
          <p>
            Skriv variabler som <code>{'{patientnamn}'}</code> direkt i texten.
            När kallelsen skickas byts de automatiskt ut mot patientens och
            bokningens riktiga uppgifter. Mallen fylls redan från start med ett
            komplett exempel som du kan bygga vidare på.
          </p>
        </div>

        {Object.entries(groupedVariables).map(([group, vars]) => (
          <div key={group} className="variable-group">
            <h4>{group}</h4>
            {vars.map(v => (
              <button
                key={v.variable}
                type="button"
                className="variable-button"
                onClick={() => insertVariable(v.variable)}
                title={v.description}
              >
                <span className="variable-name">{v.variable}</span>
                <span className="variable-desc">{v.description}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default KallelseTemplateForm;
